// Whether the prompt should ask for another line, decided on the page's own side of the worker. /python has to ask the
// interpreter, because nothing on that page can parse Python; this page is written in the language it is running, so an
// unfinished line never starts an engine. What it reads is what quickjs's own REPL reads: a line is unfinished while
// something it opened is still open, and a bracket is a bracket in TypeScript too.

export function unfinished(source: string): boolean {
  const brackets: string[] = [];
  // Each entry is a template literal one level out, waiting for the substitution below it to close.
  const templates: number[] = [];
  // The last character that could end a value, which is the whole of what tells a division from a regular expression.
  let value = false;

  for (let at = 0; at < source.length; at++) {
    const char = source[at];
    const next = source[at + 1];

    if (char === "/" && next === "/") {
      at = end(source, at, "\n");
      value = false;
    } else if (char === "/" && next === "*") {
      const closed = source.indexOf("*/", at + 2);
      if (closed === -1) return true;
      at = closed + 1;
      value = false;
    } else if (char === "\"" || char === "'") {
      const closed = quoted(source, at, char);
      if (closed === -1) return true;
      at = closed;
      value = true;
    } else if (char === "`") {
      templates.push(brackets.length);
      const closed = template(source, at);
      if (closed === -1) return true;
      // A template that ran into a substitution leaves the reading of it to the brackets below.
      if (source[closed] === "{") {
        brackets.push("{");
        at = closed;
        value = false;
        continue;
      }
      templates.pop();
      at = closed;
      value = true;
    } else if (char === "/" && !value) {
      const closed = regex(source, at);
      if (closed === -1) return true;
      at = closed;
      value = true;
    } else if (char === "(" || char === "[" || char === "{") {
      brackets.push(char);
      value = false;
    } else if (char === ")" || char === "]" || char === "}") {
      if (brackets.length > 0) brackets.pop();
      // The brace that closed a substitution hands the line back to the template it was written in.
      if (char === "}" && templates.length > 0 && templates[templates.length - 1] === brackets.length) {
        templates.pop();
        const closed = resume(source, at);
        if (closed === -1) return true;
        if (source[closed] === "{") {
          templates.push(brackets.length);
          brackets.push("{");
          at = closed;
          value = false;
          continue;
        }
        at = closed;
      }
      value = true;
    } else if (WORD.test(char)) {
      // A name or a number is a value, and a keyword is a place a regular expression may open rather than divide.
      value = !KEYWORD_END.test(source.slice(0, at + 1));
    } else if (!WHITESPACE.test(char)) {
      // An operator, after which nothing is left to divide and a slash is the start of a literal.
      value = false;
    }
  }

  return brackets.length > 0 || templates.length > 0;
}

// The index of the quote that closed the string, or -1 if the line ran out first.
function quoted(source: string, from: number, quote: string): number {
  for (let at = from + 1; at < source.length; at++) {
    if (source[at] === "\\") at++;
    else if (source[at] === quote) return at;
    else if (source[at] === "\n") return -1;
  }
  return -1;
}

// The index of the backtick that closed the template, or of the brace opening a substitution, or -1 if neither came.
function template(source: string, from: number): number {
  return resume(source, from);
}

function resume(source: string, from: number): number {
  for (let at = from + 1; at < source.length; at++) {
    if (source[at] === "\\") at++;
    else if (source[at] === "`") return at;
    else if (source[at] === "$" && source[at + 1] === "{") return at + 1;
  }
  return -1;
}

// The index of the slash that closed the literal, or -1 if the line ran out inside it.
function regex(source: string, from: number): number {
  let inClass = false;
  for (let at = from + 1; at < source.length; at++) {
    const char = source[at];
    if (char === "\\") at++;
    else if (char === "\n") return -1;
    else if (char === "[") inClass = true;
    else if (char === "]") inClass = false;
    else if (char === "/" && !inClass) return at;
  }
  return -1;
}

function end(source: string, from: number, mark: string): number {
  const at = source.indexOf(mark, from);
  return at === -1 ? source.length : at;
}

const WHITESPACE = /\s/;

const WORD = /[\w$]/;

// The words after which a slash opens a regular expression rather than dividing whatever came before it.
const KEYWORD_END = /(?:^|[^\w$])(?:return|typeof|instanceof|in|of|new|delete|void|case|do|else|yield|await|throw)$/;
